import { useState } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { FormLabel } from '../../components/ui/FormLabel';

const SupplierForm = ({ supplier, onClose, onSuccess }) => {
  const isEdit = !!supplier;

  const [formData, setFormData] = useState({
    supplier_name: supplier?.supplier_name || '',
    supplier_code: supplier?.supplier_code || '',
    contact_name: supplier?.contact_name || '',
    email: supplier?.email || '',
    phone: supplier?.phone || '',
    mobile: supplier?.mobile || '',
    address_line1: supplier?.address_line1 || '',
    address_line2: supplier?.address_line2 || '',
    city: supplier?.city || '',
    county: supplier?.county || '',
    postcode: supplier?.postcode || '',
    country: supplier?.country || 'GB',
    payment_terms: supplier?.payment_terms || '',
    credit_limit: supplier?.credit_limit || '',
    current_balance: supplier?.current_balance || '',
    account_number: supplier?.account_number || '',
    notes: supplier?.notes || '',
    is_active: supplier?.is_active !== undefined ? supplier.is_active : true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        await api.put(`/suppliers/${supplier.id}`, formData);
      } else {
        await api.post('/suppliers', formData);
      }
      onSuccess();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          'Failed to save supplier'
      );
      console.error('Form error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Supplier Name */}
      <div>
        <FormLabel required>Supplier Name</FormLabel>
        <Input
          type="text"
          name="supplier_name"
          value={formData.supplier_name}
          onChange={handleChange}
          required
          placeholder="ABC Electrical Supplies Ltd"
          className="h-8"
        />
      </div>

      {/* Supplier Code */}
      <div>
        <FormLabel>Supplier Code</FormLabel>
        <Input
          type="text"
          name="supplier_code"
          value={formData.supplier_code}
          onChange={handleChange}
          placeholder="ABC-001"
          className="h-8"
        />
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <FormLabel>Contact Name</FormLabel>
          <Input
            type="text"
            name="contact_name"
            value={formData.contact_name}
            onChange={handleChange}
            placeholder="John Smith"
            className="h-8"
          />
        </div>
        <div>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="supplier@example.com"
            className="h-8"
          />
        </div>
        <div>
          <FormLabel>Phone</FormLabel>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="01234 567890"
            className="h-8"
          />
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-h2 text-[var(--ds-text-primary)]">Address</h3>
        <div>
          <FormLabel>Address Line 1</FormLabel>
          <Input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="123 Main Street"
            className="h-8"
          />
        </div>
        <div>
          <FormLabel>Address Line 2</FormLabel>
          <Input
            type="text"
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Unit 5, Industrial Estate"
            className="h-8"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>City</FormLabel>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Manchester"
              className="h-8"
            />
          </div>
          <div>
            <FormLabel>County</FormLabel>
            <Input
              type="text"
              name="county"
              value={formData.county}
              onChange={handleChange}
              placeholder="Greater Manchester"
              className="h-8"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>Postcode</FormLabel>
            <Input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              placeholder="M1 1AA"
              className="h-8"
            />
          </div>
          <div>
            <FormLabel>Country</FormLabel>
            <Input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="GB"
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Financial Terms */}
      <div className="space-y-4">
        {isEdit &&
          formData.current_balance !== undefined &&
          formData.current_balance !== '' && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md p-4">
              <FormLabel className="mb-1">Current Balance</FormLabel>
              <p
                className={`text-xs font-semibold ${
                  parseFloat(formData.current_balance || 0) >= 0
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                £{parseFloat(formData.current_balance || 0).toFixed(2)}
              </p>
            </div>
          )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <FormLabel>Payment Terms</FormLabel>
            <Input
              type="text"
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleChange}
              placeholder="Net 30"
              className="h-8"
            />
          </div>
          <div>
            <FormLabel>Credit Limit (£)</FormLabel>
            <Input
              type="number"
              step="0.01"
              name="credit_limit"
              value={formData.credit_limit}
              onChange={handleChange}
              placeholder="5000.00"
              className="h-8"
            />
          </div>
          <div>
            <FormLabel>Current Balance (£)</FormLabel>
            <Input
              type="number"
              step="0.01"
              name="current_balance"
              value={formData.current_balance}
              onChange={handleChange}
              placeholder="0.00"
              className="h-8"
            />
          </div>
        </div>
        <div>
          <FormLabel>Account Number</FormLabel>
          <Input
            type="text"
            name="account_number"
            value={formData.account_number}
            onChange={handleChange}
            placeholder="ACC-12345"
            className="h-8"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <FormLabel>Notes</FormLabel>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Additional notes about this supplier..."
        />
      </div>

      {/* Active Status (only for edit) */}
      {isEdit && (
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="mr-2 h-4 w-4 text-[var(--ds-accent-primary)] border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)]"
            />
            <FormLabel className="mb-0">Active</FormLabel>
          </label>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--ds-border-divider)]">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={loading}
        >
          {loading ? 'Saving...' : isEdit ? 'Update Supplier' : 'Create Supplier'}
        </Button>
      </div>
    </form>
  );
};

export default SupplierForm;
